<template>
  <div class="meeting-list-container">
    <!-- 状态筛选标签 -->
    <div class="status-tabs" v-if="showTabs">
      <van-tabs v-model:active="activeTab" @change="handleTabChange">
        <van-tab title="全部会议"></van-tab>
        <van-tab title="待开展"></van-tab>
        <van-tab title="邀约中"></van-tab>
        <van-tab title="已结束"></van-tab>
      </van-tabs>
    </div>
    
    <!-- 搜索框 -->
    <div class="search-box" v-if="showSearch">
      <van-search
        v-model="searchKeyword"
        placeholder="请输入您想搜索的讲者或会议ID"
        shape="round"
        @search="handleSearch"
        @clear="handleClear"
      />
    </div>

    <!-- 会议列表 -->
    <div class="list-container">
      <div v-if="loading" class="loading-container">
        <van-loading type="spinner" color="#145294" />
        <span class="loading-text">加载中...</span>
      </div>

      <div v-else-if="meetings.length === 0" class="empty-container">
        <van-empty description="暂无预约会议" />
      </div>

      <van-list
        v-else
        v-model:loading="localLoadingMore"
        :finished="finished"
        :offset="100"
        @load="onLoad"
        class="meeting-list"
        :immediate-check="false"
        finished-text="没有更多了"
      >
        <MeetingListItem
          v-for="meeting in meetings"
          :key="meeting.id"
          :meeting="meeting"
          :is-confirmation-page="isConfirmationPage"
          @click="handleMeetingClick(meeting)"
          @modify="handleModifyMeeting"
          @cancel="handleCancelMeeting"
          @confirm-attend="handleConfirmAttend"
          @cannot-attend="handleCannotAttend"
        />
      </van-list>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, nextTick, defineExpose } from 'vue';
import { showDialog } from 'vant';
import MeetingListItem from './MeetingListItem.vue';
import { useRouter } from 'vue-router';

const props = defineProps({
  showTabs: {
    type: Boolean,
    default: true
  },
  showSearch: {
    type: Boolean,
    default: true
  },
  initialStatus: {
    type: Number,
    default: 0 // 0:全部, 1:待开展, 2:邀约中, 3:已结束
  },
  isConfirmationPage: {
    type: Boolean,
    default: false
  },
  meetings: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingMore: {
    type: Boolean,
    default: false
  },
  finished: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['meeting-click', 'meeting-modify', 'meeting-cancel', 'confirm-attend', 'cannot-attend', 'tab-change', 'load-more', 'search']);
const router = useRouter();
const activeTab = ref(props.initialStatus); // 当前选中的标签
const searchKeyword = ref('');
const localLoadingMore = ref(false);
const listContainer = ref(null); // 列表容器引用

// 根据当前选中的标签获取对应的status参数
const statusMap = {
  0: '', // 全部
  1: 'pending', // 待开展/待审批
  2: 'booking', // 邀约中
  3: 'completed' // 已结束
};

// 暴露方法给父组件
const setSearchKeyword = (value) => {
  searchKeyword.value = value;
  emit('search', value);
};

// 处理搜索
const handleSearch = (value) => {
  searchKeyword.value = value;
  emit('search', value);
};

// 处理清除搜索
const handleClear = () => {
  searchKeyword.value = '';
  emit('search', '');
};

// 处理标签切换
const handleTabChange = (index) => {
  activeTab.value = index;
  emit('tab-change', index);
};

// 处理会议点击
const handleMeetingClick = (meeting) => {
  emit('meeting-click', meeting);
};

// 处理修改会议
const handleModifyMeeting = (meeting) => {
  emit('meeting-modify', meeting);
  console.log('修改会议：', meeting);
};

// 处理取消会议
const handleCancelMeeting = (meeting) => {
  // 只触发事件，由父组件处理具体的取消逻辑和确认对话框
  emit('meeting-cancel', meeting);
};

// 处理确认参会
const handleConfirmAttend = (meeting) => {
  showDialog({
    title: '确认参会',
    message: '您确定要参加这个会议吗？',
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    showCancelButton: true
  }).then(() => {
    // 只触发事件，由父组件处理具体的确认参会逻辑
    emit('confirm-attend', meeting);
  }).catch(() => {
    // 用户取消操作
  });
};

// 处理无法参会
const handleCannotAttend = (meeting) => {
  showDialog({
    title: '无法参会',
    message: '您确定无法参加这个会议吗？',
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    showCancelButton: true
  }).then(() => {
    // 只触发事件，由父组件处理具体的无法参会逻辑
    emit('cannot-attend', meeting);
  }).catch(() => {
    // 用户取消操作
  });
};

// 处理加载更多
const onLoad = () => {
  emit('load-more');
};

// 监听loadingMore属性变化
watch(() => props.loadingMore, (newVal) => {
  localLoadingMore.value = newVal;
});

// 监听initialStatus属性变化，确保当父组件传递的initialStatus变化时，子组件的activeTab也能相应更新
watch(() => props.initialStatus, (newVal, oldVal) => {
  activeTab.value = newVal;
});

// 监听meetings属性变化，保存和恢复滚动位置
watch(() => props.meetings, (newMeetings, oldMeetings) => {
  // 只有在加载更多（新数组长度大于旧数组长度）时才需要保存和恢复滚动位置
  if (oldMeetings && newMeetings.length > oldMeetings.length && listContainer.value) {
    // 保存当前滚动位置
    const scrollTop = listContainer.value.scrollTop;
    const scrollHeight = listContainer.value.scrollHeight;
    
    // 在下一个DOM更新周期后恢复滚动位置
    nextTick(() => {
      if (listContainer.value) {
        // 计算新的滚动位置，保持相对位置不变
        const newScrollHeight = listContainer.value.scrollHeight;
        const diff = newScrollHeight - scrollHeight;
        listContainer.value.scrollTop = scrollTop + diff;
      }
    });
  }
}, { deep: true });

// 暴露方法给父组件
defineExpose({
  setSearchKeyword
});
</script>

<style scoped>


.meeting-list-container {
  font-family: 'PingFang SC', '微软雅黑', sans-serif;
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 状态筛选标签样式 */
.status-tabs {
  padding-top: 0px;
  background-color: #fff;
}

/* 搜索框样式 */
.van-search {
  padding: 8px !important;
}

/* 会议列表容器样式 */
.list-container {
  flex: 1;
  overflow-y: auto;
  padding: 0 15px;
}

/* 加载状态样式 */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.loading-text {
  margin-top: 12px;
  color: #666;
  font-size: 14px;
}

/* 空状态样式 */
.empty-container {
  padding: 40px 0;
}

/* 会议列表样式 */
.meeting-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 适配Vant List组件的样式 */
:deep(.van-list__loading) {
  padding: 20px 0;
}

:deep(.van-list__finished-text) {
  color: #999;
  font-size: 14px;
  padding: 20px 0;
}
</style>